/**
 * Blog CMS — admin editor (/naytive-blog)
 *
 * List + create/edit blog posts. AI-generate from a prompt (Claude/Bedrock),
 * write/edit in a lightweight WYSIWYG, upload cover + inline images to S3,
 * AI-assisted SEO, manage categories. Published posts feed the standalone
 * naytive.com/blogs site.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { naytiveAPI } from '../services/api';

const emptyDraft = {
  id: null, title: '', slug: '', excerpt: '', contentHtml: '', coverImageUrl: '',
  categoryId: '', status: 'draft', authorName: '', metaTitle: '', metaDescription: '',
  keywords: [], ogImageUrl: '',
};

export default function NaytiveBlog() {
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [seoBusy, setSeoBusy] = useState(false);
  const [catPanel, setCatPanel] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [toast, setToast] = useState(null);
  const editorRef = useRef(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2600); };

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listBlogPosts(
        statusFilter === 'all' ? {} : { status: statusFilter },
      );
      setPosts(r.data?.items || []);
    } catch { setPosts([]); } finally { setLoading(false); }
  }, [statusFilter]);

  const loadCategories = useCallback(async () => {
    try { const r = await naytiveAPI.listBlogCategories(); setCategories(r.data?.items || []); }
    catch { setCategories([]); }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const openNew = () => { setDraft(emptyDraft); setView('edit'); setTimeout(() => editorRef.current?.setHtml(''), 0); };

  const openEdit = async (id) => {
    try {
      const r = await naytiveAPI.getBlogPost(id);
      const it = r.data?.item;
      const d = {
        id: it.id, title: it.title || '', slug: it.slug || '', excerpt: it.excerpt || '',
        contentHtml: it.content_html || '', coverImageUrl: it.cover_image_url || '',
        categoryId: it.category_id || '', status: it.status || 'draft', authorName: it.author_name || '',
        metaTitle: it.meta_title || '', metaDescription: it.meta_description || '',
        keywords: it.keywords || [], ogImageUrl: it.og_image_url || '',
      };
      setDraft(d);
      setView('edit');
      setTimeout(() => editorRef.current?.setHtml(d.contentHtml), 0);
    } catch { flash('Failed to load post'); }
  };

  const upd = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async (publish) => {
    const contentHtml = editorRef.current?.getHtml() ?? draft.contentHtml;
    const body = { ...draft, contentHtml, status: publish ? 'published' : draft.status };
    if (!body.title.trim()) { flash('Title required'); return; }
    body.categoryId = body.categoryId || null;
    setSaving(true);
    try {
      if (draft.id) {
        await naytiveAPI.updateBlogPost(draft.id, body);
      } else {
        const r = await naytiveAPI.createBlogPost(body);
        upd({ id: r.data?.id });
      }
      flash(publish ? 'Published' : 'Saved');
      await loadPosts();
      if (publish) setView('list');
      else if (draft.status !== body.status) upd({ status: body.status });
    } catch (e) {
      flash(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try { await naytiveAPI.deleteBlogPost(id); await loadPosts(); flash('Deleted'); }
    catch { flash('Delete failed'); }
  };

  const runAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    try {
      const cat = categories.find((c) => String(c.id) === String(draft.categoryId));
      const r = await naytiveAPI.blogAiGenerate({ prompt: aiPrompt.trim(), category: cat?.name });
      const g = r.data?.draft || {};
      setDraft((d) => ({
        ...d,
        title: g.title || d.title,
        slug: g.slug || d.slug,
        excerpt: g.excerpt || d.excerpt,
        contentHtml: g.contentHtml || d.contentHtml,
        metaTitle: g.metaTitle || d.metaTitle,
        metaDescription: g.metaDescription || d.metaDescription,
        keywords: g.keywords?.length ? g.keywords : d.keywords,
      }));
      setTimeout(() => editorRef.current?.setHtml(g.contentHtml || ''), 0);
      setAiOpen(false);
      setAiPrompt('');
      flash('Draft generated');
    } catch (e) {
      flash(e.response?.data?.message || 'AI generation failed');
    } finally { setAiBusy(false); }
  };

  const runSeo = async () => {
    setSeoBusy(true);
    try {
      const contentHtml = editorRef.current?.getHtml() ?? draft.contentHtml;
      const r = await naytiveAPI.blogAiSeo({ title: draft.title, contentHtml });
      const s = r.data?.seo || {};
      setDraft((d) => ({
        ...d,
        metaTitle: s.metaTitle || d.metaTitle,
        metaDescription: s.metaDescription || d.metaDescription,
        keywords: s.keywords?.length ? s.keywords : d.keywords,
        slug: d.slug || s.slug || d.slug,
      }));
      flash('SEO suggestions applied');
    } catch (e) {
      flash(e.response?.data?.message || 'AI SEO failed');
    } finally { setSeoBusy(false); }
  };

  const uploadCover = async (file) => {
    try {
      const fileBase64 = await fileToBase64(file);
      const r = await naytiveAPI.uploadBlogImage({ fileBase64, fileName: file.name, mime: file.type });
      upd({ coverImageUrl: r.data?.url });
    } catch { flash('Cover upload failed'); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await naytiveAPI.createBlogCategory({ name: newCat.trim() }); setNewCat(''); await loadCategories(); }
    catch { flash('Category failed'); }
  };
  const delCategory = async (id) => {
    if (!window.confirm('Delete this category? Posts keep, but lose their category.')) return;
    try { await naytiveAPI.deleteBlogCategory(id); await loadCategories(); await loadPosts(); }
    catch { flash('Delete failed'); }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ padding: '24px 28px' }}>
          {view === 'list' ? (
            <>
              <div style={hdr}>
                <div>
                  <h1 style={h1}>Blog</h1>
                  <div style={sub}>Write, generate with AI, and publish posts to naytive.com/blogs.</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnGhost} onClick={() => setCatPanel(true)}>Categories</button>
                  <button style={btnPrimary} onClick={openNew}>New post</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, margin: '16px 0' }}>
                {['all', 'draft', 'published'].map((s) => (
                  <button key={s} onClick={() => setStatusFilter(s)} style={{ ...chip, ...(statusFilter === s ? chipOn : null) }}>
                    {s}
                  </button>
                ))}
              </div>

              {loading ? (
                <div style={empty}>Loading…</div>
              ) : posts.length === 0 ? (
                <div style={empty}>No posts yet. Hit "New post" to write or AI-generate one.</div>
              ) : (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB', textAlign: 'left', color: '#6B7280' }}>
                        <th style={th}>Title</th><th style={th}>Category</th><th style={th}>Status</th><th style={th}>Updated</th><th style={th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((p) => (
                        <tr key={p.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                          <td style={td}>
                            <div style={{ fontWeight: 600, color: '#111827' }}>{p.title}</div>
                            <div style={{ fontSize: 12, color: '#9CA3AF' }}>/{p.slug}</div>
                          </td>
                          <td style={td}>{p.category_name || '—'}</td>
                          <td style={td}>
                            <span style={{ ...pill, ...(p.status === 'published' ? pillPub : pillDraft) }}>{p.status}</span>
                          </td>
                          <td style={td}>{fmt(p.updated_at)}</td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button style={miniBtn} onClick={() => openEdit(p.id)}>Edit</button>
                            <button style={{ ...miniBtn, color: '#B91C1C', marginLeft: 6 }} onClick={() => del(p.id)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            /* ── EDITOR ── */
            <>
              <div style={hdr}>
                <button style={btnGhost} onClick={() => { setView('list'); loadPosts(); }}>← Back</button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnGhost} onClick={() => setAiOpen(true)}>✨ AI generate</button>
                  <button style={btnGhost} onClick={() => save(false)} disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</button>
                  <button style={btnPrimary} onClick={() => save(true)} disabled={saving}>Publish</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 20, marginTop: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Main column */}
                <div style={{ flex: 1, minWidth: 420 }}>
                  <input
                    style={titleInput}
                    placeholder="Post title"
                    value={draft.title}
                    onChange={(e) => upd({ title: e.target.value })}
                  />
                  <RichTextEditor ref={editorRef} onImageUpload={naytiveAPI.uploadBlogImage} />
                </div>

                {/* Sidebar column */}
                <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Panel title="Publish">
                    <Row label="Status">
                      <span style={{ ...pill, ...(draft.status === 'published' ? pillPub : pillDraft) }}>{draft.status}</span>
                    </Row>
                    <label style={lbl}>Slug</label>
                    <input style={inp} value={draft.slug} onChange={(e) => upd({ slug: e.target.value })} placeholder="auto from title" />
                    <label style={lbl}>Author</label>
                    <input style={inp} value={draft.authorName} onChange={(e) => upd({ authorName: e.target.value })} placeholder="Naytive Team" />
                    <label style={lbl}>Category</label>
                    <select style={inp} value={draft.categoryId} onChange={(e) => upd({ categoryId: e.target.value })}>
                      <option value="">— none —</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </Panel>

                  <Panel title="Cover image">
                    {draft.coverImageUrl ? (
                      <img src={draft.coverImageUrl} alt="cover" style={{ width: '100%', borderRadius: 8, marginBottom: 8 }} />
                    ) : null}
                    <FilePick label={draft.coverImageUrl ? 'Replace cover' : 'Upload cover'} onFile={uploadCover} />
                  </Panel>

                  <Panel title="Excerpt">
                    <textarea style={{ ...inp, height: 64, resize: 'vertical' }} value={draft.excerpt}
                      onChange={(e) => upd({ excerpt: e.target.value })} placeholder="1–2 sentence summary shown on cards" />
                  </Panel>

                  <Panel title="SEO" action={<button style={miniBtn} onClick={runSeo} disabled={seoBusy}>{seoBusy ? '…' : '✨ AI SEO'}</button>}>
                    <label style={lbl}>Meta title</label>
                    <input style={inp} value={draft.metaTitle} onChange={(e) => upd({ metaTitle: e.target.value })} maxLength={70} />
                    <label style={lbl}>Meta description</label>
                    <textarea style={{ ...inp, height: 56, resize: 'vertical' }} value={draft.metaDescription}
                      onChange={(e) => upd({ metaDescription: e.target.value })} maxLength={170} />
                    <label style={lbl}>Keywords (comma-separated)</label>
                    <input style={inp} value={(draft.keywords || []).join(', ')}
                      onChange={(e) => upd({ keywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                  </Panel>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* AI generate modal */}
      {aiOpen && (
        <div style={overlay} onClick={() => !aiBusy && setAiOpen(false)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>Generate with AI</h2>
            <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 12 }}>
              Describe the post — Claude drafts the title, body (HTML), excerpt + SEO. You add images.
            </div>
            <textarea
              style={{ ...inp, height: 120, resize: 'vertical' }}
              placeholder="e.g. A guide comparing Delaware C-Corp vs Wyoming LLC for early-stage founders raising VC"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button style={btnGhost} onClick={() => setAiOpen(false)} disabled={aiBusy}>Cancel</button>
              <button style={btnPrimary} onClick={runAi} disabled={aiBusy || !aiPrompt.trim()}>
                {aiBusy ? 'Generating… (~30s)' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories modal */}
      {catPanel && (
        <div style={overlay} onClick={() => setCatPanel(false)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Categories</h2>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <input style={inp} value={newCat} onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }} placeholder="New category name" />
              <button style={btnPrimary} onClick={addCategory}>Add</button>
            </div>
            {categories.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9CA3AF' }}>No categories yet.</div>
            ) : categories.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid #F3F4F6' }}>
                <span style={{ fontSize: 13, color: '#111827' }}>{c.name} <span style={{ color: '#9CA3AF' }}>· {c.post_count || 0} posts</span></span>
                <button style={{ ...miniBtn, color: '#B91C1C' }} onClick={() => delCategory(c.id)}>Delete</button>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button style={btnGhost} onClick={() => setCatPanel(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  );
}

// ── Lightweight WYSIWYG (contentEditable + execCommand) ──────────────────
const RichTextEditor = forwardRef(({ onImageUpload }, ref) => {
  const elRef = useRef(null);
  useImperativeHandle(ref, () => ({
    setHtml: (html) => { if (elRef.current) elRef.current.innerHTML = html || ''; },
    getHtml: () => elRef.current?.innerHTML || '',
  }));

  const cmd = (command, value = null) => {
    elRef.current?.focus();
    document.execCommand(command, false, value);
  };
  const heading = (tag) => cmd('formatBlock', tag);
  const link = () => { const url = window.prompt('Link URL'); if (url) cmd('createLink', url); };
  const insertImage = async (file) => {
    try {
      const fileBase64 = await fileToBase64(file);
      const r = await onImageUpload({ fileBase64, fileName: file.name, mime: file.type });
      const url = r.data?.url;
      if (url) { elRef.current?.focus(); document.execCommand('insertHTML', false, `<img src="${url}" alt="" style="max-width:100%;border-radius:8px" />`); }
    } catch { /* ignore */ }
  };

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <div style={toolbar}>
        <TB onClick={() => heading('H2')}>H2</TB>
        <TB onClick={() => heading('H3')}>H3</TB>
        <TB onClick={() => heading('P')}>¶</TB>
        <Sep />
        <TB onClick={() => cmd('bold')} style={{ fontWeight: 700 }}>B</TB>
        <TB onClick={() => cmd('italic')} style={{ fontStyle: 'italic' }}>I</TB>
        <Sep />
        <TB onClick={() => cmd('insertUnorderedList')}>• List</TB>
        <TB onClick={() => cmd('insertOrderedList')}>1. List</TB>
        <TB onClick={() => heading('BLOCKQUOTE')}>❝</TB>
        <Sep />
        <TB onClick={link}>🔗 Link</TB>
        <FilePick label="🖼 Image" inline onFile={insertImage} />
      </div>
      <div
        ref={elRef}
        contentEditable
        suppressContentEditableWarning
        style={editorArea}
      />
    </div>
  );
});

function FilePick({ label, onFile, inline }) {
  const id = useRef(`fp-${Math.random().toString(36).slice(2)}`).current;
  return (
    <>
      <label htmlFor={id} style={inline ? { ...tbBtn, cursor: 'pointer' } : { ...btnGhost, display: 'inline-block', cursor: 'pointer', textAlign: 'center' }}>
        {label}
      </label>
      <input id={id} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </>
  );
}

const Panel = ({ title, action, children }) => (
  <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', padding: 14 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: '#6B7280' }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);
const Row = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
    <span style={{ fontSize: 12.5, color: '#6B7280' }}>{label}</span>{children}
  </div>
);
const TB = ({ children, onClick, style }) => (
  <button onClick={onClick} type="button" style={{ ...tbBtn, ...style }}>{children}</button>
);
const Sep = () => <span style={{ width: 1, height: 18, background: '#E5E7EB', margin: '0 2px' }} />;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
function fmt(v) { if (!v) return '—'; const d = new Date(v); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(); }

// ── styles ──
const hdr = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' };
const h1 = { fontSize: 22, fontWeight: 600, margin: 0, color: '#111827' };
const sub = { fontSize: 13, color: '#6B7280', marginTop: 3 };
const btnPrimary = { padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2540D9', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnGhost = { padding: '8px 14px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const chip = { padding: '6px 14px', border: '1px solid #E5E7EB', borderRadius: 999, background: '#fff', color: '#6B7280', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' };
const chipOn = { background: '#EEF2FF', color: '#2540D9', borderColor: '#C7D2FE' };
const th = { padding: '11px 14px', fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' };
const td = { padding: '12px 14px', color: '#374151', verticalAlign: 'top' };
const pill = { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, textTransform: 'capitalize' };
const pillPub = { background: '#D1FAE5', color: '#065F46' };
const pillDraft = { background: '#FEF3C7', color: '#92400E' };
const miniBtn = { fontSize: 12, padding: '4px 9px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' };
const empty = { padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', marginTop: 4 };
const titleInput = { width: '100%', boxSizing: 'border-box', fontSize: 24, fontWeight: 600, border: 'none', outline: 'none', color: '#111827', padding: '4px 0 14px', fontFamily: 'inherit' };
const toolbar = { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' };
const tbBtn = { padding: '4px 9px', border: '1px solid transparent', borderRadius: 6, background: 'transparent', color: '#374151', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' };
const editorArea = { minHeight: 420, padding: '18px 20px', outline: 'none', fontSize: 15, lineHeight: 1.7, color: '#1F2937', fontFamily: 'inherit' };
const lbl = { display: 'block', fontSize: 11.5, fontWeight: 500, color: '#6B7280', margin: '10px 0 4px' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '8px 11px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.4)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 };
const modal = { width: 'min(540px,100%)', background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', maxHeight: '85vh', overflowY: 'auto' };
const toastStyle = { position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 200 };
